import { AUTOMATION_STEPS } from '../steps';
import UUIDUtil from 'utility/UUID';

export const defaultValidationState = { show: false, type: null };

export const VALIDATION_STATE_TYPES = {
  GO_TO_STEP_LOOP: 'go_to_step_loop',
};

// A skipped go to step never jumps, so it can't create a loop
const isGoToStep = (e) =>
  e.type === AUTOMATION_STEPS.go_to_automation_step.type &&
  !e.shouldSkipExecution;

const parentChecker = (arr, parentKey) => {
  const parentItem = arr.find((e) => e.id === parentKey);
  if (!parentItem) return false;

  if (
    parentItem.type === AUTOMATION_STEPS.delay.type ||
    parentItem.type === AUTOMATION_STEPS.goal.type
  )
    return true;

  if (!parentItem.parentKey) return false;
  return parentChecker(arr, parentItem.parentKey);
};

const buildBranches = (steps) => {
  const branchMap = new Map();

  const worker = (steps, step, stepsInPath) => {
    const parentItem = steps.find((e) => e.id === step.parentKey);
    const path = parentItem ? stepsInPath.concat(parentItem) : stepsInPath;
    if (!parentItem) {
      return path;
    }
    return worker(steps, parentItem, path);
  };

  steps.forEach((el) => {
    branchMap.set(el.id, worker(steps, el, [el]).reverse());
  });

  return branchMap;
};

const checkBranches = (
  branches,
  steps,
  gotoStepId,
  nextGotoTargetStep,
  path
) => {
  // Process each qualifying branch slice independently to avoid false positives
  // from merging multiple branches into a single flat array.
  const branchSlices = Array.from(branches.entries())
    .filter(
      ([, way]) =>
        way.some((e) => e.id === nextGotoTargetStep?.id) && way.some(isGoToStep)
    )
    .map(([, way]) =>
      [...way].splice(way.findIndex((e) => e.id === nextGotoTargetStep?.id))
    );

  for (const ways of branchSlices) {
    const newPath = path.concat(ways);

    const inLoop = newPath.find((el) => el.id === gotoStepId);
    const nextGoTo = ways.find(isGoToStep);

    if (!nextGoTo || nextGoTo.details.triggerKey) {
      continue;
    }

    const nextStep = steps.find(
      (el) => el.id === nextGoTo.details.stepKey?.value
    );
    const nextStepInLoop = newPath.find(
      (el) => el.id === nextGotoTargetStep.id
    );

    if (inLoop) {
      return newPath;
    }
    if (nextStepInLoop) {
      const waysEndsWithGoto =
        ways.length > 0 ? isGoToStep(ways[ways.length - 1]) : false;

      if (waysEndsWithGoto) {
        const lastStep = ways[ways.length - 1];

        const newNextStep = steps.find(
          (el) => el.id === lastStep.details.stepKey?.value
        );

        const newNewNextStepInLoop = newPath.find(
          (el) => el.id === newNextStep?.id
        );

        if (!newNewNextStepInLoop) {
          const result = checkBranches(
            branches,
            steps,
            gotoStepId,
            newNextStep,
            newPath
          );
          if (result) return result;
          continue;
        }
      }
      return newPath;
    }

    if (nextGotoTargetStep?.id === nextStep?.id) {
      continue;
    }

    const result = checkBranches(
      branches,
      steps,
      gotoStepId,
      nextStep,
      newPath
    );
    if (result) {
      return result;
    }
  }

  return null;
};

/**
 * function check all gotoStep which goto another actions in the end we build full way step by step
 * if we meet a "goto", we find out nextGotoTargetStep step, then we check all possible branches for this step and find out if some branch contain another "goto" or same "goto"
 * if we meet same "goto" = we have a circle, and we should validate this
 * if we meet another "goto" = repeat first step
 * if we don't meet any "goto" and steps ended, we are good
 *
 * Our main goal find out "goto" which will be inside a circle of steps
 * @param {Array} all steps
 * @param {Object} step step goto
 * @param {Map} all possible branches based on steps
 * @returns {Boolean} validation result
 */

const goToActionChecker = (steps, gotoStep, branches) => {
  const nextGotoTarget = steps.find(
    (el) => el.id === gotoStep.details.stepKey?.value
  );

  if (!nextGotoTarget) {
    return false;
  }

  const ways = Array.from(branches.entries())
    .filter(
      ([, way]) =>
        way.some((e) => e.id === nextGotoTarget.id) && way.some(isGoToStep)
    )
    .map(([, way]) =>
      [...way].splice(way.findIndex((e) => e.id === nextGotoTarget.id))
    );

  return ways
    .map((way) => {
      const inLoop = way.find((el) => el?.id === gotoStep.id);

      const nextGoTo = way.find(isGoToStep);

      if (!nextGoTo || nextGoTo.details.triggerKey) {
        return null; // that means we jumps to trigger
      }
      const nextGotoTargetStep = steps.find(
        (el) => el.id === nextGoTo.details.stepKey?.value
      );
      const nextStepInLoop = way.find((el) => el.id === nextGotoTargetStep?.id);

      if (inLoop || nextStepInLoop) {
        return [gotoStep].concat(way);
      }

      return [gotoStep].concat(
        checkBranches(branches, steps, gotoStep.id, nextGotoTargetStep, way)
      );
    })
    .reduce((acc, path) => {
      if (acc) {
        return acc; // skip if we already had failed before
      }

      if (!path || path.includes(null)) {
        return false;
      }

      // if we have delay or goal in the way, we are good
      return !path.some(
        (e) =>
          e.type === AUTOMATION_STEPS.delay.type ||
          e.type === AUTOMATION_STEPS.goal.type
      );
    }, false);
};

export const VALIDATION = {
  [VALIDATION_STATE_TYPES.GO_TO_STEP_LOOP]: ({ steps }) => {
    const branches = buildBranches(steps);

    // get all the goto steps
    return steps.filter((step) => {
      if (!isGoToStep(step)) {
        return false;
      }

      if (step.details.triggerKey) {
        // if goto_step jumps to trigger
        return !parentChecker(steps, step.parentKey);
      }
      // check the action steps
      return goToActionChecker(steps, step, branches);
    });
  },
};

export const isValidStepId = (stepId) =>
  //should match either a uuid generated by BE or new step id genereted by FE as `new.${step.type}.${Date.now()}`
  UUIDUtil.validate(stepId) || /^new\.[a-z_]*\.\d{13,16}$/.test(stepId);
